/**
 * PriceAlertsPanel — create, view and manage price alerts.
 */

import { memo, useState, useCallback } from 'react';
import { useTerminalStore } from '../../stores/terminal.store.js';
import { generateId } from '@arc/shared';
import type { PriceAlertCondition } from '@arc/shared';

// ─── Alert row ────────────────────────────────────────────────────────────────

function AlertRow({
  alert,
  currentPrice,
  onRemove,
}: {
  alert: ReturnType<typeof useTerminalStore.getState>['priceAlerts'][number];
  currentPrice?: number;
  onRemove: () => void;
}) {
  const fired       = alert.firedAt !== undefined;
  const progress    = currentPrice && alert.targetPrice > 0
    ? alert.condition === 'above'
      ? Math.min(100, (currentPrice / alert.targetPrice) * 100)
      : Math.min(100, (alert.targetPrice / currentPrice) * 100)
    : 0;

  return (
    <div className={`border p-2 flex flex-col gap-1 transition-all ${
      fired ? 'border-buy/30 bg-buy/5' : 'border-hud bg-black/20'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={`font-mono text-[11px] font-bold ${
            alert.condition === 'above' ? 'text-buy' : 'text-sell'
          }`}>
            {alert.condition === 'above' ? '▲' : '▼'}
          </span>
          <span className="font-orb font-bold text-[11px]" style={{ color: 'var(--color-bright)' }}>
            {alert.symbol}
          </span>
          <span className="font-mono text-[11px] opacity-90">
            {alert.condition.toUpperCase()} ${alert.targetPrice.toFixed(alert.targetPrice < 1 ? 6 : 2)}
          </span>
          {alert.repeat && (
            <span className="font-mono text-[11px] border border-arc/30 px-1 opacity-90"
                  style={{ color: 'var(--color-arc)' }}>
              REPEAT
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {fired && (
            <span className="font-mono text-[11px] text-buy">✓ FIRED</span>
          )}
          <button
            onClick={onRemove}
            className="font-mono text-[11px] text-sell/60 hover:text-sell transition-colors px-1"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {!fired && currentPrice !== undefined && (
        <div className="relative h-0.5 bg-hud/30 rounded-full overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(100, progress)}%`,
              background: alert.condition === 'above'
                ? 'linear-gradient(90deg, rgba(0,255,136,0.3), rgba(0,255,136,0.8))'
                : 'linear-gradient(90deg, rgba(255,60,60,0.3), rgba(255,60,60,0.8))',
            }}
          />
        </div>
      )}

      {/* Current vs target */}
      {!fired && currentPrice !== undefined && (
        <div className="flex justify-between font-mono text-[11px] opacity-90">
          <span>Now: ${currentPrice.toFixed(currentPrice < 1 ? 6 : 2)}</span>
          <span>
            {alert.condition === 'above'
              ? `${((alert.targetPrice - currentPrice) / currentPrice * 100).toFixed(2)}% away`
              : `${((currentPrice - alert.targetPrice) / currentPrice * 100).toFixed(2)}% away`
            }
          </span>
        </div>
      )}

      {alert.note && (
        <div className="font-mono text-[11px] opacity-90 truncate">{alert.note}</div>
      )}
    </div>
  );
}

// ─── Add alert form ───────────────────────────────────────────────────────────

function AddAlertForm({ onAdd }: { onAdd: () => void }) {
  const tokens    = useTerminalStore(s => s.tokens);
  const prices    = useTerminalStore(s => s.prices);
  const addAlert  = useTerminalStore(s => s.addPriceAlert);

  const [symbol,    setSymbol]    = useState('BTC');
  const [condition, setCondition] = useState<PriceAlertCondition>('above');
  const [target,    setTarget]    = useState('');
  const [note,      setNote]      = useState('');
  const [repeat,    setRepeat]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  const curPrice = prices[symbol]?.priceUsd;

  const handleAdd = useCallback(() => {
    const targetNum = parseFloat(target);
    if (isNaN(targetNum) || targetNum <= 0) {
      setError('Enter a valid target price');
      return;
    }
    setError(null);
    addAlert({
      id:          generateId(),
      symbol,
      condition,
      targetPrice: targetNum,
      createdAt:   Date.now(),
      repeat,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    setTarget('');
    setNote('');
    onAdd();
  }, [symbol, condition, target, note, repeat, addAlert, onAdd]);

  const fillCurrent = () => {
    if (curPrice) setTarget(curPrice.toFixed(curPrice < 1 ? 6 : 2));
  };

  return (
    <div className="flex flex-col gap-2 p-2 border-t border-hud">
      <div className="font-mono text-[11px] tracking-widest opacity-90">+ NEW ALERT</div>

      {/* Symbol */}
      <div className="grid grid-cols-2 gap-1">
        <select
          value={symbol}
          onChange={e => setSymbol(e.target.value)}
          className="bg-black/40 border border-hud font-orb font-bold text-[11px] px-2 py-1.5 outline-none"
          style={{ color: 'var(--color-arc)' }}
        >
          {tokens.map(t => (
            <option key={t.symbol} value={t.symbol} className="bg-[#040d1a]">{t.symbol}</option>
          ))}
        </select>

        {/* Condition */}
        <div className="grid grid-cols-2 gap-0.5">
          {(['above', 'below'] as PriceAlertCondition[]).map(c => (
            <button
              key={c}
              onClick={() => setCondition(c)}
              className={`font-mono text-[11px] py-1 border tracking-wider transition-all ${
                condition === c
                  ? c === 'above'
                    ? 'border-buy text-buy bg-buy/10'
                    : 'border-sell text-sell bg-sell/10'
                  : 'border-hud hover:border-arc'
              }`}
              style={condition === c ? undefined : { color: 'var(--color-bright)' }}
            >
              {c === 'above' ? '▲ ABOVE' : '▼ BELOW'}
            </button>
          ))}
        </div>
      </div>

      {/* Target price */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <label className="font-mono text-[11px] tracking-widest opacity-90">TARGET PRICE</label>
          {curPrice && (
            <button
              onClick={fillCurrent}
              className="font-mono text-[11px] opacity-40 hover:opacity-70 underline"
            >
              ${curPrice.toFixed(curPrice < 1 ? 4 : 2)}
            </button>
          )}
        </div>
        <input
          type="number"
          value={target}
          onChange={e => setTarget(e.target.value)}
          className="bg-black/40 border border-hud font-mono text-[11px] px-2 py-1.5 outline-none w-full"
          style={{ color: 'var(--color-bright)' }}
          placeholder="0.00"
          step="any"
          min="0"
        />
      </div>

      {/* Note */}
      <input
        type="text"
        value={note}
        onChange={e => setNote(e.target.value)}
        className="bg-black/40 border border-hud font-mono text-[11px] px-2 py-1 outline-none w-full"
        style={{ color: 'var(--color-bright)' }}
        placeholder="Note (optional)"
        maxLength={60}
      />

      {/* Repeat */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={repeat}
          onChange={e => setRepeat(e.target.checked)}
          className="accent-arc"
        />
        <span className="font-mono text-[11px] opacity-90">Repeat after firing</span>
      </label>

      {error && (
        <div className="font-mono text-[11px] text-sell">{error}</div>
      )}

      <button
        onClick={handleAdd}
        className="font-mono text-[11px] py-1.5 border border-arc/60 tracking-widest hover:bg-arc/10 transition-all"
        style={{ color: 'var(--color-arc)' }}
      >
        + SET ALERT
      </button>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export const PriceAlertsPanel = memo(function PriceAlertsPanel() {
  const priceAlerts   = useTerminalStore(s => s.priceAlerts);
  const prices        = useTerminalStore(s => s.prices);
  const removePriceAlert = useTerminalStore(s => s.removePriceAlert);

  const [showForm, setShowForm] = useState(false);

  const active = priceAlerts.filter(a => a.firedAt === undefined);
  const fired  = priceAlerts.filter(a => a.firedAt !== undefined);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-hud flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] opacity-90">ACTIVE</span>
          <span className="font-orb font-bold text-[11px]" style={{ color: 'var(--color-arc)' }}>
            {active.length}
          </span>
          {fired.length > 0 && (
            <>
              <span className="font-mono text-[11px] opacity-90">FIRED</span>
              <span className="font-mono text-[11px] text-buy">{fired.length}</span>
            </>
          )}
        </div>
        <button
          onClick={() => setShowForm(p => !p)}
          className={`font-mono text-[11px] px-2 py-0.5 border tracking-wider transition-all ${
            showForm
              ? 'border-arc text-arc bg-arc/10'
              : 'border-hud hover:border-arc'
          }`}
          style={{ color: showForm ? 'var(--color-arc)' : 'var(--color-bright)' }}
        >
          {showForm ? '✕ CANCEL' : '+ NEW'}
        </button>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Active alerts */}
        <div className="p-2 flex flex-col gap-1.5">
          {active.length === 0 && !showForm && (
            <div className="flex flex-col items-center justify-center py-8 gap-2 opacity-30">
              <div className="text-[24px]">🔔</div>
              <span className="font-mono text-[11px] tracking-widest">NO ACTIVE ALERTS</span>
              <span className="font-mono text-[11px]">Click + NEW to set one</span>
            </div>
          )}

          {active.map(alert => {
            const cp = prices[alert.symbol]?.priceUsd;
            return (
              <AlertRow
                key={alert.id}
                alert={alert}
                {...(cp !== undefined ? { currentPrice: cp } : {})}
                onRemove={() => removePriceAlert(alert.id)}
              />
            );
          })}

          {/* Fired alerts (dimmed) */}
          {fired.length > 0 && (
            <>
              <div className="font-mono text-[11px] opacity-90 tracking-widest mt-2 mb-1">FIRED</div>
              {fired.slice(0, 5).map(alert => {
                const cp = prices[alert.symbol]?.priceUsd;
                return (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    {...(cp !== undefined ? { currentPrice: cp } : {})}
                    onRemove={() => removePriceAlert(alert.id)}
                  />
                );
              })}
            </>
          )}
        </div>

        {/* New alert form */}
        {showForm && <AddAlertForm onAdd={() => setShowForm(false)} />}
      </div>
    </div>
  );
});
